import { FC, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cn from 'classnames';
import { useBoolean, useMediaQuery } from 'usehooks-ts';
import { compact, sumBy, values } from 'lodash';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer, Icon } from '@ebsco-ui/ebsco-ui';

import { FacetKey, facetsOrder, FeatureFlagDisplay } from '@app/components';
import { useSearchContext } from '@app/contexts';
import { sharedMessages } from '@app/translations';
import { breakpoint, Feature } from '@app/constants';

import { FilterPanelContext } from './FilterPanelContext';
import { ActiveFacetAttributes } from './ActiveFacetAttributes';
import { Facets } from './Facets';
import { PublicationYearFacet } from './PublicationYearFacet';
import { useFacetsRequest } from './hooks';

import css from './FilterPanel.module.scss';

export const FilterPanel: FC = () => {
  const { $t } = useIntl();
  const { activeFacets, setActiveFacets } = useSearchContext();
  const { value: isDrawerOpen, toggle: toggleIsDrawerOpen } = useBoolean(false);
  const [isFooterOverlapped, setIsFooterOverlapped] = useState(false);
  const [activePublicationYearFacetCount, setActivePublicationYearFacetCount] =
    useState(0);
  const [facetsToFetch, setFacetsToFetch] = useState<FacetKey[]>([]);
  const [isStatePristine, setStatePristine] = useState(true);
  const facets = useFacetsRequest({
    expandedFacets: facetsToFetch,
    isStatePristine,
  });
  const isDesktop = useMediaQuery(
    `(min-width: ${breakpoint.SCREEN.DESKTOP_SM}px)`
  );

  const getActiveFacetAttributesCount = useCallback(
    facetKey => compact(values(activeFacets[facetKey])).length,
    [activeFacets]
  );
  const activeFacetAttributesCount = sumBy(facetsOrder, facetKey =>
    getActiveFacetAttributesCount(facetKey)
  );
  const refineResultsButtonId = 'refineResultsButton';
  const totalActiveFacetAttributesCount =
    activeFacetAttributesCount + activePublicationYearFacetCount;

  const handleScroll = useCallback(({ target }) => {
    const { clientHeight, scrollHeight, scrollTop } = target;
    const shouldOverlap = scrollHeight - Math.round(scrollTop) > clientHeight;

    setIsFooterOverlapped(shouldOverlap);
  }, []);

  const filterIcon =
    totalActiveFacetAttributesCount > 0
      ? {
          ...Icon.ICONS.FILTERS_ON,
          title: $t({
            id: 'filterPane.facetsOn',
            defaultMessage: 'Facets are selected',
          }),
        }
      : {
          ...Icon.ICONS.FILTERS_OFF,
          title: $t({
            id: 'filterPane.facetsOff',
            defaultMessage: 'Facets are not selected',
          }),
        };

  const filterPanelTitle = (
    <h2 className={css.title} data-auto="filter-panel-title">
      <Icon {...filterIcon} />
      {!totalActiveFacetAttributesCount ? (
        <FormattedMessage
          id="filterPane.title"
          defaultMessage="Refine results"
        />
      ) : (
        <FormattedMessage
          id="filterPane.titlePlural"
          defaultMessage="Refine results ({count})"
          values={{ count: totalActiveFacetAttributesCount }}
        />
      )}
    </h2>
  );

  const toggleFacetAttribute = useCallback(
    (facetKey, value) => {
      setActiveFacets(prevValue => ({
        ...prevValue,
        [facetKey]: {
          ...prevValue[facetKey],
          [value]: !prevValue[facetKey][value],
        },
      }));
    },
    [setActiveFacets]
  );

  const filterPanelContent = (
    <FilterPanelContext.Provider
      value={{
        facets,
        facetsToFetch,
        activeFacetAttributesCount,
        toggleFacetAttribute,
        setFacetsToFetch,
        setStatePristine,
        getActiveFacetAttributesCount,
      }}
    >
      <section
        aria-label={$t({
          id: 'filterPane.sectionAriaLabel',
          defaultMessage: 'Search filters',
        })}
        className={css.filterPanelContent}
        data-auto="filter-panel"
      >
        <div className={css.filterPanelHeader}>
          {filterPanelTitle}
          {!isDesktop && (
            <Button
              styleType="flat-icon"
              className={css.closeButton}
              aria-label={$t(sharedMessages.close)}
              onClick={toggleIsDrawerOpen}
            >
              <FontAwesomeIcon className="fa-lg" icon={faTimes} />
            </Button>
          )}
        </div>
        {totalActiveFacetAttributesCount > 0 && (
          <ActiveFacetAttributes
            facets={facets}
            onFacetAttributeRemoval={toggleFacetAttribute}
          />
        )}
        <div
          data-auto="filter-panel-body"
          className={css.filterPanelBody}
          onScroll={handleScroll}
        >
          <Facets />
          <FeatureFlagDisplay
            flagToMatch={{ name: Feature.publicationYearFacet }}
          >
            <PublicationYearFacet
              activeFacetAttributesCount={activePublicationYearFacetCount}
              setActiveFacetAttributesCount={setActivePublicationYearFacetCount}
            />
          </FeatureFlagDisplay>
        </div>
        {!isDesktop && (
          <div
            className={cn(css.filterPanelFooter, {
              [css.isFooterOverlapping]: isFooterOverlapped,
            })}
          >
            <Button
              styleType="solid"
              className={css.seeResultsButton}
              onClick={toggleIsDrawerOpen}
            >
              <FormattedMessage
                id="filterPane.seeResults"
                defaultMessage="See results"
              />
            </Button>
          </div>
        )}
      </section>
    </FilterPanelContext.Provider>
  );

  if (isDesktop) {
    return filterPanelContent;
  }

  return (
    <>
      <div className={css.refineResultsButtonWrapper}>
        <button
          className={css.refineResultsButton}
          id={refineResultsButtonId}
          type="button"
          aria-expanded={isDrawerOpen}
          onClick={toggleIsDrawerOpen}
        >
          {filterPanelTitle}
        </button>
      </div>
      <Drawer
        drawerTag="section"
        className={css.drawer}
        position="start"
        titleElementId={refineResultsButtonId}
        isOpen={isDrawerOpen}
        onClose={toggleIsDrawerOpen}
      >
        {filterPanelContent}
      </Drawer>
    </>
  );
};
